import { memo } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import NotFoundImage from "@/assets/not-found.webp";
import { borderRadius, glassEffect, shadows, spacingConstants, transitionStyles } from "theme-gaia";

interface TerritoryNotFoundProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  showAction?: boolean;
  actionLabel?: string;
  actionTo?: string;
  mode?: "page" | "embedded";
}

const TerritoryIcon = () => (
  <Box
    className="icon-float"
    component="img"
    src={NotFoundImage} 
    sx={{
      width: { xs: 120, md: 160 },
      height: 'auto',
      filter: "grayscale(0.2) sepia(0.2)", 
      mb: spacingConstants.md
    }}
  />
);

const TerritoryNotFound = memo(function TerritoryNotFound({
  title = (
    <>
      Territorio <br />
      <Box component="span" sx={{ color: "primary.main" }}>
        no encontrado
      </Box>
    </>
  ),
  description = (
    <>
      La Entidad Territorial Indígena (ETI) que buscas no se encuentra en
      nuestro sistema. Esto puede deberse a un error en el enlace o el
      territorio aún no ha sido creado.
    </>
  ),
  showAction = true,
  actionLabel = "Elegir otra ETI",
  actionTo = "/",
  mode = "page",

 
}: TerritoryNotFoundProps) {
  const isPage = mode === "page";
  return (
    <Box
      className="bg-territory-pattern"
      sx={{
        width: "100%",
        overflow: "hidden",

        ...(isPage
          ? {
            height: "100dvh",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }
          : {
            position: "relative",
            height: "100%",
            minHeight: { md: "100%" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: spacingConstants.lg, md: 0 },
          }),
      }}
    >
      <Container maxWidth={isPage ? "sm" : false}>
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={spacingConstants.md}
          sx={{
            ...glassEffect,
            py: {xs: spacingConstants.lg, md: spacingConstants.lg},
            px: {xs: spacingConstants.md, md: spacingConstants.lg},
            borderRadius: borderRadius.lg,
            textAlign: "center",
            boxShadow: shadows.md,
            
          }}
        >
          {/* Icono Principal */}
          <Box className="reveal-item">
            <TerritoryIcon />
          </Box>

          {/* Título  */}
          <Box className="reveal-item delay-1">
            <Typography
              variant="h1lgBold"
              component="h1"
              sx={{
                textTransform: "uppercase",
                color: "text.primary"
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Descripción */}
          <Box className="reveal-item delay-2">
            <Typography
              variant="h3Medium"
              sx={{
                color: "text.primary",
                maxWidth: "400px",
                mx: "auto",
                textWrap: "balance"
              }}
            >
              {description}
            </Typography>
          </Box>

          {/* Acción */}
          <Box className="reveal-item delay-3">
            {showAction && (
            <Button
              component={Link}
              to={actionTo}
              variant="contained"
              startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                bgcolor: "green.button", 
                color: "text.secondary",
                px: 4,
                py: 1.5,
                borderRadius: borderRadius.pill,
                transition: transitionStyles.smooth,
                "&:hover": {
                  bgcolor: "primary.main",
                  transform: "scale(1.02)",
                  boxShadow: shadows.md,
                }
              }}
            >
              <Typography variant="h3lgSemibold" color="inherit">
                {actionLabel}
              </Typography>
            </Button>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
});

export default TerritoryNotFound;